// File: ./src/components/ModUpgradeCalculator.js
import React, { useState, useMemo, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSound } from '../contexts/SoundContext';
import {
  formatNumber,
  generateModCostKey,
  calculateModUpgradeCost,
  calculateModUpgradeSteps,
} from '../utils/dataUtils';
import Clarity from '@microsoft/clarity';

const MAX_MOD_CURRENT_LEVEL = 5;
const MAX_MOD_TARGET_LEVEL = 6;
const MAX_MOD_DATA_LEVEL = 6;

const rarityColorMap = {
  epic: '#C084FC',
  legendary: '#FDE047',
};

const MECH_RARITIES = [
  { label: 'Common',    factor: 0.50, color: '#9CA3AF' },
  { label: 'Uncommon',  factor: 0.60, color: '#6EE7B7' },
  { label: 'Rare',      factor: 0.70, color: '#60A5FA' },
  { label: 'Epic',      factor: 0.85, color: '#C084FC' },
  { label: 'Legendary', factor: 1.00, color: '#FDE047' },
];

const ModUpgradeCalculator = ({ mods, costs }) => {
  const { t } = useLanguage();
  const { playButtonClick, playButtonHover, playSelectOptionOpen, playInterfaceSelectionHover } =
    useSound();

  const assetBase = typeof window !== 'undefined' ? window.location.origin : '';

  const [rarityFilter, setRarityFilter] = useState('all'); // all | epic | legendary
  const [modQuery, setModQuery] = useState('');
  const [isModListOpen, setIsModListOpen] = useState(false);
  const [selectedMod, setSelectedMod] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [targetLevel, setTargetLevel] = useState(6);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [steps, setSteps] = useState([]);

  const filteredMods = useMemo(() => {
    return (mods || [])
      .filter((m) => {
        if (rarityFilter === 'all') return true;
        return (m.rarity || '').toLowerCase() === rarityFilter;
      })
      .filter((m) => {
        const q = (modQuery || '').toLowerCase();
        if (!q) return true;
        return (
          (m.mod_label || '').toLowerCase().includes(q) ||
          (m.mod_name || '').toLowerCase().includes(q) ||
          (m.rarity || '').toLowerCase().includes(q)
        );
      });
  }, [mods, rarityFilter, modQuery]);

  const selectedModRarityColor = useMemo(() => {
    if (!selectedMod) return '#6b7280';
    const key = (selectedMod.rarity || '').toLowerCase();
    return rarityColorMap[key] || '#6b7280';
  }, [selectedMod]);

  const getModImage = useCallback(
    (mod) => {
      if (!mod) return `${assetBase}/mod_images/basic_mod_parts.png`;
      const rarity = (mod.rarity || '').toLowerCase();
      const name = mod.mod_name || '';
      const fileName = `${rarity}_${name}.png`;
      return `${assetBase}/mod_images/${fileName}`;
    },
    [assetBase]
  );

  const getCurrencyIcon = (currency) => {
    if (currency === 'elite_mod_parts') {
      return `${assetBase}/mod_images/elite_mod_parts.png`;
    }
    return `${assetBase}/mod_images/basic_mod_parts.png`;
  };

  const handleSelectMod = useCallback(
    (mod) => {
      playInterfaceSelectionHover();
      setSelectedMod(mod);
      setIsModListOpen(false);
      setError(null);
      setResults(null);
      setSteps([]);

      // Track mod selection in Google Analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'item_selected', {
          'item_name': mod.mod_label,
          'item_type': 'mod',
          'item_rarity': mod.rarity,
          'calculator_type': 'mod'
        });
      }
    },
    [playInterfaceSelectionHover]
  );

  const handleCalculate = useCallback(() => {
    setError(null);
    setResults(null);
    setSteps([]);

    if (!selectedMod) {
      setError('Please select a mod');
      // Track validation error
      if (typeof gtag !== 'undefined') {
        gtag('event', 'error_occurred', {
          'error_type': 'validation',
          'error_message': 'Please select a mod',
          'calculator_type': 'mod'
        });
      }
      return;
    }

    if (currentLevel < 1 || currentLevel > MAX_MOD_CURRENT_LEVEL) {
      setError(`Current level must be between 1 and ${MAX_MOD_CURRENT_LEVEL}`);
      // Track validation error
      if (typeof gtag !== 'undefined') {
        gtag('event', 'error_occurred', {
          'error_type': 'validation',
          'error_message': `Current level must be between 1 and ${MAX_MOD_CURRENT_LEVEL}`,
          'calculator_type': 'mod'
        });
      }
      return;
    }

    if (targetLevel < 2 || targetLevel > MAX_MOD_TARGET_LEVEL) {
      setError(`Target level must be between 2 and ${MAX_MOD_TARGET_LEVEL}`);
      // Track validation error
      if (typeof gtag !== 'undefined') {
        gtag('event', 'error_occurred', {
          'error_type': 'validation',
          'error_message': `Target level must be between 2 and ${MAX_MOD_TARGET_LEVEL}`,
          'calculator_type': 'mod'
        });
      }
      return;
    }

    if (targetLevel <= currentLevel) {
      setError('Target level must be higher than current level');
      // Track validation error
      if (typeof gtag !== 'undefined') {
        gtag('event', 'error_occurred', {
          'error_type': 'validation',
          'error_message': 'Target level must be higher than current level',
          'calculator_type': 'mod'
        });
      }
      return;
    }

    try {
      const costKey = generateModCostKey(selectedMod);
      const effectiveTarget = Math.min(targetLevel, MAX_MOD_DATA_LEVEL);
      const total = calculateModUpgradeCost(costs, costKey, currentLevel, effectiveTarget);
      const breakdown = calculateModUpgradeSteps(costs, costKey, currentLevel, effectiveTarget);

      const mechRarityCosts = MECH_RARITIES.map(({ label, factor, color }) => ({
        label,
        color,
        basic_mod_parts: Math.ceil(total.basic_mod_parts * factor),
        elite_mod_parts: Math.ceil(total.elite_mod_parts * factor),
      }));

      setResults({
        mod: selectedMod,
        from: `L ${currentLevel}`,
        to: `L ${targetLevel}`,
        total,
        mechRarityCosts,
      });
      setSteps(breakdown);
      
      // Track calculation completion in Google Analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'calculation_completed', {
          'item_name': selectedMod.mod_label,
          'item_type': 'mod',
          'item_rarity': selectedMod.rarity,
          'from_level': currentLevel,
          'to_level': targetLevel,
          'total_basic_mod_parts': total.basic_mod_parts,
          'total_elite_mod_parts': total.elite_mod_parts,
          'calculator_type': 'mod'
        });
      }

      // Track in Clarity and upgrade session priority
      if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
        Clarity.event('mod_calculation_completed');
        Clarity.upgrade('mod_calculation_completed');
      }
    } catch (e) {
      setError(`Calculation error: ${e.message}`);
      // Track calculation error
      if (typeof gtag !== 'undefined') {
        gtag('event', 'error_occurred', {
          'error_type': 'calculation',
          'error_message': e.message,
          'calculator_type': 'mod'
        });
      }
    }
  }, [selectedMod, currentLevel, targetLevel, costs]);

  return (
    <div className="bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-[#121212] dark:via-[#1E1E1E] dark:to-[#1A1A1A] rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 border border-blue-100 dark:border-[#2A2A2A] max-w-4xl mx-auto transition-colors duration-300">
      <div className="relative mb-6 sm:mb-8 text-center rounded-2xl overflow-hidden
        bg-white/70 backdrop-blur-sm border border-indigo-100
        shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_3px_rgba(99,102,241,0.08)]
        dark:bg-gradient-to-br dark:from-[#FF7A32] dark:via-[#FF5722] dark:to-[#D84018]
        dark:border-[#FF8A65]/40
        dark:shadow-[0_0_20px_rgba(255,120,50,0.3),0_0_40px_rgba(255,87,34,0.15)]">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-indigo-500 via-blue-500 to-indigo-400 dark:hidden" />
        <div className="p-5 sm:p-6 lg:p-7 dark:p-8">
          <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-indigo-400 dark:text-orange-200/80 mb-1.5">Mech Arena</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
            Upgrade Calculator
          </h2>
        </div>
      </div>

      {/* Rarity Filter */}
      <div className="mb-4">
        <label className="block text-xs sm:text-sm font-medium text-indigo-700 dark:text-white mb-1">
          Rarity
        </label>
        <select
          value={rarityFilter}
          onChange={(e) => {
            setRarityFilter(e.target.value);
            setIsModListOpen(true);
            // Track filter change
            if (typeof gtag !== 'undefined') {
              gtag('event', 'filter_changed', {
                'filter_type': 'rarity',
                'filter_value': e.target.value,
                'calculator_type': 'mod'
              });
            }
          }}
          className="w-full px-3 py-2 rounded-lg border-2 border-indigo-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:border-indigo-500 dark:focus:border-[#FF8A65] transition-colors"
        >
          <option value="all">All</option>
          <option value="epic">Epic</option>
          <option value="legendary">Legendary</option>
        </select>
      </div>

      {/* Mod selection */}
      <div className="mb-6">
        <label className="block text-sm sm:text-base font-medium text-indigo-700 dark:text-white mb-2">
          Select Mod
        </label>
        <div className="relative">
          <div className="flex items-center rounded-xl border-2 border-indigo-200 dark:border-gray-600 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-gray-500 hover:shadow-md">
            <svg
              className="w-5 h-5 text-indigo-400 dark:text-[#FFB38A] ml-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
              />
            </svg>
            <input
              type="text"
              value={modQuery}
              placeholder={selectedMod ? selectedMod.mod_label : 'Search mods...'}
              onFocus={() => {
                if (!isModListOpen) {
                  playSelectOptionOpen();
                }
                setIsModListOpen(true);
              }}
              onChange={(e) => {
                if (!isModListOpen) {
                  playSelectOptionOpen();
                }
                setModQuery(e.target.value);
                setIsModListOpen(true);
                // Track search query (only if query length > 2 to avoid noise)
                if (typeof gtag !== 'undefined' && e.target.value.length > 2) {
                  gtag('event', 'search_performed', {
                    'search_term_length': e.target.value.length,
                    'calculator_type': 'mod'
                  });
                }
              }}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl outline-none text-sm sm:text-base bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              aria-label="Search mods"
            />
            {selectedMod && (
              <button
                type="button"
                className="mr-3 text-indigo-500 dark:text-[#FF7043] text-xs underline hover:text-indigo-700 dark:hover:text-[#FF8A65]"
                onClick={() => {
                  playButtonClick();
                  setSelectedMod(null);
                  setModQuery('');
                  setIsModListOpen(true);
                  setResults(null);
                  setSteps([]);
                }}
              >
                Clear
              </button>
            )}
          </div>

          {isModListOpen && (
            <div className="absolute z-20 mt-2 w-full max-h-64 overflow-auto bg-white dark:bg-gray-800 border-2 border-indigo-200 dark:border-gray-600 rounded-xl shadow-lg">
              {filteredMods.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No results</div>
              ) : (
                filteredMods.map((mod) => (
                  <button
                    type="button"
                    key={`${mod.rarity}_${mod.mod_name}_${mod.purchase_currency}`}
                    onClick={() => handleSelectMod(mod)}
                    className={`w-full text-left px-4 py-2 text-sm sm:text-base hover:bg-indigo-50 dark:hover:bg-[#2F1E13] ${
                      selectedMod &&
                      selectedMod.rarity === mod.rarity &&
                      selectedMod.mod_name === mod.mod_name &&
                      selectedMod.purchase_currency === mod.purchase_currency
                        ? 'bg-indigo-50 dark:bg-[#432615]'
                        : ''
                    } text-gray-900 dark:text-gray-100`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {mod.mod_label}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {mod.rarity} • {mod.purchase_currency}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selected mod summary */}
      {selectedMod && (
        <div className="mt-3 mb-6 p-3 bg-blue-50 dark:bg-white/10 rounded-lg border border-blue-200 dark:border-[#FF8A65] flex items-center gap-4">
          <div className="flex-shrink-0">
            <img
              src={getModImage(selectedMod)}
              alt={selectedMod.mod_label}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border-2 object-contain bg-white dark:bg-[#1A1A1A] p-1"
              style={{ borderColor: selectedModRarityColor }}
              onError={(e) => {
                if (!e.target.dataset.fallback) {
                  e.target.dataset.fallback = '1';
                  e.target.src = getCurrencyIcon(selectedMod.purchase_currency);
                }
              }}
            />
          </div>
          <div className="text-xs sm:text-sm text-blue-700 dark:text-blue-300 flex-1 space-y-1">
            <div className="font-semibold text-sm sm:text-base text-blue-900 dark:text-blue-100">
              {selectedMod.mod_label}
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] sm:text-xs">
              <span
                className="inline-flex items-center gap-1 bg-white/80 dark:bg-[#1A1A1A]/70 px-2 py-1 rounded-lg border text-slate-800 dark:text-white"
                style={{ borderColor: selectedModRarityColor }}
              >
                Rarity: {selectedMod.rarity}
              </span>
            </div>
            <div className="pt-1 flex items-center flex-wrap gap-1.5">
              <strong>Purchase Cost:</strong>
              <span className="flex items-center gap-1.5">
                <img
                  src={getCurrencyIcon(selectedMod.purchase_currency)}
                  alt={selectedMod.purchase_currency}
                  className="w-5 h-5 sm:w-6 sm:h-6"
                />
                {formatNumber(selectedMod.purchase_cost || 0)}{' '}
                <span className="text-xs text-gray-600">
                  ({selectedMod.purchase_currency.replace(/_/g, ' ')})
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Current and Target level selectors */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        <div>
          <label className="block text-sm sm:text-base font-medium text-indigo-700 dark:text-white mb-2">
            Current Level
          </label>
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: MAX_MOD_CURRENT_LEVEL }, (_, idx) => idx + 1).map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => {
                  playInterfaceSelectionHover();
                  setCurrentLevel(level);
                  // Track level change
                  if (typeof gtag !== 'undefined') {
                    gtag('event', 'level_changed', {
                      'level_type': 'current',
                      'level_value': level,
                      'calculator_type': 'mod'
                    });
                  }
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                  currentLevel === level
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-emerald-200 dark:border-gray-600 hover:border-emerald-400'
                }`}
              >
                L{level}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:items-end">
          <label className="block text-sm sm:text-base font-medium text-indigo-700 dark:text-white mb-2 sm:text-right">
            Target Level
          </label>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            {Array.from({ length: MAX_MOD_TARGET_LEVEL - 1 }, (_, idx) => idx + 2).map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => {
                  playInterfaceSelectionHover();
                  setTargetLevel(level);
                  // Track level change
                  if (typeof gtag !== 'undefined') {
                    gtag('event', 'level_changed', {
                      'level_type': 'target',
                      'level_value': level,
                      'calculator_type': 'mod'
                    });
                  }
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                  targetLevel === level
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-indigo-200 dark:border-gray-600 hover:border-indigo-400'
                }`}
              >
                L{level}
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 mb-4">
        Calculating from <span className="font-semibold">Level {currentLevel}</span> to{' '}
        <span className="font-semibold">Level {targetLevel}</span>.
      </p>

      {/* Calculate button */}
      <button
        onClick={() => {
          playButtonClick();
          handleCalculate();
        }}
        onMouseEnter={playButtonHover}
        className="w-full py-3 sm:py-4 px-4 sm:px-6 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:ring-offset-2 text-base sm:text-lg font-semibold shadow-lg bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white"
      >
        Calculate Mod Cost
      </button>

      {/* Error */}
      {error && (
        <div className="mt-4 sm:mt-6 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300 text-red-700 px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-md">
          <div className="flex items-center">
            <svg className="w-4 sm:w-5 h-4 sm:h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm sm:text-base">{error}</span>
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="mt-6 sm:mt-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-[#0F1F0F] dark:to-[#0A1A0A] border-2 border-green-200 dark:border-[#2A3A2A] rounded-2xl p-4 sm:p-6 shadow-lg">
          <h3 className="text-lg sm:text-xl font-bold text-green-800 dark:text-green-300 mb-1 text-center">
            Total Mod Parts Needed
          </h3>
          <p className="text-sm text-green-700 dark:text-green-400 mb-4 text-center">
            {results.mod.mod_label.trim()}: {results.from} → {results.to}
          </p>

          {/* Per-mech-rarity cost table */}
          <div className="overflow-x-auto rounded-xl border border-green-200 dark:border-[#333] shadow-sm">
            <table className="w-full text-sm bg-white dark:bg-[#1A1A1A]">
              <thead>
                <tr className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-[#1E2A1E] dark:to-[#1A2A1A]">
                  <th className="px-4 py-3 text-left font-semibold text-green-800 dark:text-green-300 border-b border-green-200 dark:border-[#333]">
                    Mech Rarity
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-blue-700 dark:text-blue-300 border-b border-green-200 dark:border-[#333]">
                    <div className="flex items-center justify-end gap-1.5">
                      <img src={`${assetBase}/mod_images/basic_mod_parts.png`} alt="Basic" className="w-5 h-5" />
                      Basic Parts
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-purple-700 dark:text-purple-300 border-b border-green-200 dark:border-[#333]">
                    <div className="flex items-center justify-end gap-1.5">
                      <img src={`${assetBase}/mod_images/elite_mod_parts.png`} alt="Elite" className="w-5 h-5" />
                      Elite Parts
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.mechRarityCosts.map(({ label, color, basic_mod_parts, elite_mod_parts }) => {
                  const isLegendary = label === 'Legendary';
                  return (
                    <tr
                      key={label}
                      className={`border-b border-green-100 dark:border-[#2A2A2A] transition-colors ${
                        isLegendary
                          ? 'bg-yellow-50 dark:bg-[#2A2410]'
                          : 'hover:bg-green-50 dark:hover:bg-[#1E2A1E]'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: color }}
                          />
                          <span className={`font-medium dark:text-white ${isLegendary ? 'text-yellow-800 font-semibold' : 'text-gray-800'}`}>
                            {label}
                          </span>
                          {isLegendary && (
                            <span className="text-[10px] text-yellow-600 dark:text-yellow-400 font-medium">(base)</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${isLegendary ? 'text-blue-800 dark:text-blue-200' : 'text-blue-600 dark:text-blue-400'}`}>
                          {formatNumber(basic_mod_parts)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${isLegendary ? 'text-purple-800 dark:text-purple-200' : 'text-purple-600 dark:text-purple-400'}`}>
                          {formatNumber(elite_mod_parts)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Breakdown */}
          {steps.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <p className="text-xs text-green-700 dark:text-green-400 mb-2 font-medium">Step-by-step breakdown (Legendary mech base cost)</p>
              <table className="w-full text-sm border border-green-200 dark:border-[#333] rounded-lg bg-white dark:bg-[#1A1A1A] shadow-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-[#1E2A1E] dark:to-[#1A2A1A]">
                    <th className="px-3 py-3 text-left border-b border-green-200 dark:border-[#333] font-semibold text-green-800 dark:text-green-300">
                      Step
                    </th>
                    <th className="px-3 py-3 text-right border-b border-green-200 dark:border-[#333] font-semibold text-blue-700 dark:text-blue-300">
                      Basic
                    </th>
                    <th className="px-3 py-3 text-right border-b border-green-200 dark:border-[#333] font-semibold text-blue-800 dark:text-blue-200">
                      Total Basic
                    </th>
                    <th className="px-3 py-3 text-right border-b border-green-200 dark:border-[#333] font-semibold text-purple-700 dark:text-purple-300">
                      Elite
                    </th>
                    <th className="px-3 py-3 text-right border-b border-green-200 dark:border-[#333] font-semibold text-purple-800 dark:text-purple-200">
                      Total Elite
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {steps.map((step, index) => (
                    <tr
                      key={`${step.from}-${step.to}-${index}`}
                      className="border-b border-green-200 dark:border-[#2A2A2A] hover:bg-green-50 dark:hover:bg-[#1E2A1E] transition-colors duration-200"
                    >
                      <td className="px-3 py-3 font-medium text-gray-800 dark:text-gray-200">
                        {step.from} → {step.to}
                      </td>
                      <td className="px-3 py-3 text-right text-blue-600 dark:text-blue-400 font-medium">
                        {formatNumber(step.basic_mod_parts)}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-blue-700 dark:text-blue-300">
                        {formatNumber(step.total_basic_mod_parts)}
                      </td>
                      <td className="px-3 py-3 text-right text-purple-600 dark:text-purple-400 font-medium">
                        {formatNumber(step.elite_mod_parts)}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-purple-800 dark:text-purple-200">
                        {formatNumber(step.total_elite_mod_parts)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModUpgradeCalculator;


